import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  LayoutDashboard,
  Settings as SettingsIcon,
  FileText,
  Inbox,
  LogOut,
  HelpCircle,
  Moon,
  Sun,
} from "lucide-react";

/**
 * CommandPalette — global Cmd+K / Ctrl+K command palette.
 *
 * Self-contained:
 *   - Listens for Cmd/Ctrl+K to open, Esc to close.
 *   - Renders a backdrop + centered modal when open.
 *   - Search filters a static list of commands.
 *   - ↑/↓ to highlight, Enter to execute.
 *
 * Designed to be mounted ONCE at the app root. Owns its own visibility
 * state — parents pass `role`, `setCurrentView`, and `onSignOut` so the
 * commands know which targets are relevant to the current user.
 *
 * Customer/quote search (dynamic data) is a deliberate follow-up — this
 * version ships the keyboard shortcut + static jumps as the foundation.
 *
 * @param {Object} props
 * @param {string|null}  props.role            "tradie" | "customer" | "business" | null
 * @param {Function}     props.setCurrentView  (viewId) => void
 * @param {Function}     [props.onSignOut]
 */
export default function CommandPalette({ role, setCurrentView, onSignOut }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef(null);

  const isTradie = role === "tradie" || role === "business";
  const isCustomer = role === "customer";

  // ── Build the command list (depends on role) ────────────────────────────
  const allCommands = useMemo(() => {
    const cmds = [];
    if (isTradie) {
      cmds.push({
        id: "go-dashboard",
        label: "Go to Dashboard",
        keywords: "home today",
        icon: LayoutDashboard,
        run: () => setCurrentView("tradie"),
      });
      cmds.push({
        id: "go-settings",
        label: "Open Settings",
        keywords: "profile business templates branding theme account",
        icon: SettingsIcon,
        run: () => setCurrentView("settings"),
      });
      cmds.push({
        id: "go-templates",
        label: "Manage Quote Templates",
        keywords: "templates line items reusable settings",
        icon: FileText,
        run: () => {
          setCurrentView("settings");
          // The Settings page reads the active tab from local state — a
          // future enhancement could deep-link via URL hash. For now this
          // lands the user on Settings; one click to Templates tab.
        },
      });
    }
    if (isCustomer) {
      cmds.push({
        id: "go-customer-dashboard",
        label: "Go to Dashboard",
        keywords: "home customer",
        icon: LayoutDashboard,
        run: () => setCurrentView("customer"),
      });
      cmds.push({
        id: "go-customer-messages",
        label: "Open Messages",
        keywords: "chat inbox messages",
        icon: Inbox,
        run: () => setCurrentView("customer-messages"),
      });
      cmds.push({
        id: "go-customer-settings",
        label: "Open Settings",
        keywords: "profile account",
        icon: SettingsIcon,
        run: () => setCurrentView("customer-settings"),
      });
    }
    cmds.push({
      id: "toggle-theme",
      label: "Toggle dark / light theme",
      keywords: "dark light theme appearance",
      icon: Moon,
      run: () => {
        const root = document.documentElement;
        const isDark = root.getAttribute("data-theme") === "dark";
        if (isDark) {
          root.removeAttribute("data-theme");
          try { localStorage.setItem("theme", "light"); } catch {}
        } else {
          root.setAttribute("data-theme", "dark");
          try { localStorage.setItem("theme", "dark"); } catch {}
        }
      },
    });
    if (onSignOut && role) {
      cmds.push({
        id: "sign-out",
        label: "Sign out",
        keywords: "logout exit",
        icon: LogOut,
        run: () => onSignOut(),
      });
    }
    cmds.push({
      id: "help",
      label: "Press Cmd+K (or Ctrl+K) to open this palette anywhere",
      keywords: "help shortcut",
      icon: HelpCircle,
      run: () => {},
    });
    return cmds;
  }, [isTradie, isCustomer, role, setCurrentView, onSignOut]);

  // ── Filter by query (case-insensitive label + keywords match) ───────────
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allCommands;
    return allCommands.filter((c) => {
      const haystack = `${c.label} ${c.keywords || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [allCommands, query]);

  // ── Keep highlightIndex in range when filter shrinks ────────────────────
  useEffect(() => {
    if (highlightIndex >= visible.length) setHighlightIndex(0);
  }, [visible.length, highlightIndex]);

  // ── Global Cmd/Ctrl+K listener (always on) ──────────────────────────────
  useEffect(() => {
    function onKeydown(e) {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKeydown);
    return () => window.removeEventListener("keydown", onKeydown);
  }, [open]);

  // ── Focus input on open, reset on close ─────────────────────────────────
  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlightIndex(0);
      // Defer focus so the input is mounted.
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [open]);

  function runCommand(cmd) {
    if (!cmd) return;
    cmd.run();
    setOpen(false);
  }

  function onInputKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      runCommand(visible[highlightIndex]);
    }
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "10vh 16px 16px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "560px",
          background: "var(--surface-solid, #ffffff)",
          color: "var(--text)",
          borderRadius: "14px",
          boxShadow:
            "0 24px 64px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06) inset",
          overflow: "hidden",
        }}
      >
        {/* Search input */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "14px 16px",
            borderBottom: "1px solid var(--border, rgba(140,100,80,0.14))",
          }}
        >
          <Search size={18} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Type a command…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            autoFocus
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "var(--text)",
              fontSize: "1rem",
              padding: "4px 0",
            }}
          />
          <kbd
            style={{
              fontSize: "0.7rem",
              padding: "3px 6px",
              borderRadius: "6px",
              background: "rgba(0,0,0,0.06)",
              color: "var(--text-soft, #6b5448)",
              fontFamily: "inherit",
              letterSpacing: "0.04em",
            }}
          >
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div
          style={{
            maxHeight: "60vh",
            overflowY: "auto",
            padding: "6px 0",
          }}
        >
          {visible.length === 0 ? (
            <div
              style={{
                padding: "20px 16px",
                textAlign: "center",
                color: "var(--text-soft, #6b5448)",
                fontSize: "0.9rem",
              }}
            >
              No commands match "{query}"
            </div>
          ) : (
            visible.map((cmd, i) => {
              const Icon = cmd.icon;
              const active = i === highlightIndex;
              return (
                <button
                  key={cmd.id}
                  type="button"
                  onClick={() => runCommand(cmd)}
                  onMouseEnter={() => setHighlightIndex(i)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                    background: active
                      ? "var(--theme-accent-light, rgba(255, 138, 0,0.10))"
                      : "transparent",
                    color: active
                      ? "var(--primary, #FF8A00)"
                      : "var(--text)",
                    border: "none",
                    cursor: "pointer",
                    padding: "10px 16px",
                    fontSize: "0.92rem",
                    fontFamily: "inherit",
                    textAlign: "left",
                    transition: "background 0.12s ease, color 0.12s ease",
                  }}
                >
                  {Icon && (
                    <Icon
                      size={16}
                      strokeWidth={active ? 2.4 : 2}
                      aria-hidden="true"
                    />
                  )}
                  <span style={{ flex: 1, minWidth: 0 }}>{cmd.label}</span>
                  {active && (
                    <kbd
                      style={{
                        fontSize: "0.7rem",
                        padding: "3px 6px",
                        borderRadius: "6px",
                        background: "rgba(255, 138, 0,0.15)",
                        color: "var(--primary, #FF8A00)",
                        fontFamily: "inherit",
                        letterSpacing: "0.04em",
                      }}
                    >
                      ↵
                    </kbd>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Footer hint */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
            padding: "8px 16px",
            borderTop: "1px solid var(--border, rgba(140,100,80,0.14))",
            fontSize: "0.72rem",
            color: "var(--text-soft, #6b5448)",
            background: "var(--surface-soft, #faf5f2)",
          }}
        >
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> to navigate · <kbd>↵</kbd> to select ·
            <kbd>esc</kbd> to close
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <Sun size={11} aria-hidden="true" /> Remote Quote
          </span>
        </div>
      </div>
    </div>
  );
}
